// src/components/inputs/DateInput.jsx

import React from "react";
import { TextField, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import ar from "date-fns/locale/ar-SA"; // ✅ Arabic locale
import enGB from "date-fns/locale/en-GB"; // ✅ UK English locale
import "react-datepicker/dist/react-datepicker.css";
import "./DateInput.css";

// ✅ Register locales
registerLocale("ar", ar);
registerLocale("en-GB", enGB);

/**
 * Date Input Component
 * @param {string} label - Input label
 * @param {string} value - Date value (YYYY-MM-DD format)
 * @param {Function} onChange - Change handler
 * @param {boolean} required - Is required
 * @param {boolean} disabled - Is disabled
 * @param {string} error - Error message
 * @param {string} helperText - Helper text
 * @param {string} min - Minimum date (YYYY-MM-DD)
 * @param {string} max - Maximum date (YYYY-MM-DD)
 * @param {boolean} disablePast - Disable past dates
 * @param {Object} inputProps - Additional input props
 */
const DateInput = ({
    label,
    value = "",
    onChange,
    name,
    required = false,
    disabled = false,
    error = false,
    helperText = "",
    min = "",
    max = "",
    disablePast = false,
    inputProps = {},
    ...rest
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // ✅ Date format based on language
    const dateFormat = isArabic ? "yyyy/MM/dd" : "dd/MM/yyyy";
    const locale = isArabic ? "ar" : "en-GB";

    // ✅ تحويل string إلى Date object بطريقة آمنة
    const parseDate = (dateString) => {
        if (!dateString || dateString === '') return null;

        try {
            const cleaned = dateString.replace(/\//g, '-');
            const parts = cleaned.split('-');

            if (parts.length !== 3) return null;

            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const day = parseInt(parts[2], 10);

            const date = new Date(year, month, day);

            if (isNaN(date.getTime())) return null;

            return date;
        } catch (e) {
            console.error('Invalid date:', dateString, e);
            return null;
        }
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const finalMin = disablePast ? today : (min ? parseDate(min) : null);

    const dateValue = parseDate(value);
    const minDate = finalMin;
    const maxDate = max ? parseDate(max) : null;

    // ✅ معالجة التغيير من DatePicker
    const handleDateChange = (date) => {
        if (!onChange) {
            console.warn('⚠️ onChange is not defined');
            return;
        }

        let formattedDate = '';

        if (date && date instanceof Date && !isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            formattedDate = `${year}-${month}-${day}`;
        }

        const syntheticEvent = {
            target: {
                name: name || '',
                value: formattedDate
            }
        };

        onChange(syntheticEvent);
    };

    // ✅ Custom render for month dropdown
    const renderMonthContent = (month, shortMonth, longMonth) => {
        const monthNumber = String(month + 1).padStart(2, '0');
        return (
            <div className="custom-month-option">
                <span className="month-number">{monthNumber}</span>
                <span className="month-name">{isArabic ? longMonth : shortMonth}</span>
            </div>
        );
    };

    return (
        <DatePicker
            selected={dateValue}
            onChange={handleDateChange}
            dateFormat={dateFormat}
            locale={locale}
            minDate={minDate}
            maxDate={maxDate}
            disabled={disabled}
            placeholderText={dateFormat.toUpperCase()}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            yearDropdownItemNumber={100}
            scrollableYearDropdown
            renderMonthContent={renderMonthContent}
            calendarClassName={isDark ? "dark-calendar" : "light-calendar"}
            popperClassName="custom-datepicker-popper"
            // portalId="datepicker-portal"
            popperContainer={({ children }) => <div>{children}</div>}
            // withPortal
            portalId="root-portal"
            autoComplete="off"
            customInput={
                <TextField
                    fullWidth
                    label={label}
                    required={required}
                    error={error}
                    helperText={helperText}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    inputProps={{
                        readOnly: false,
                        autoComplete: "off",
                        ...inputProps,
                    }}
                    sx={{
                        direction: isArabic ? "rtl" : "ltr",
                        "& .MuiInputLabel-root": {
                            left: isArabic ? "auto" : undefined,
                            right: isArabic ? "1.75rem" : undefined,
                            transformOrigin: isArabic ? "right" : "left",
                        },
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                            "& input": {
                                padding: "16.5px 14px",
                                fontSize: "1rem",
                                fontFamily: theme.typography.fontFamily,
                                cursor: "pointer",
                            },
                        },
                    }}
                    {...rest}
                />
            }
        />
    );
};

export default DateInput;
